// https://chatgpt.com/c/6917f282-acb8-8328-ba9b-0542a619be32
"use client";

import { useLayoutEffect, useEffect, useRef, useState } from "react";
import Link from "next/link";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { cn } from "@/lib/utils";

type Props = {
  categorySlug?: string;
  subcategorySlug?: string;
  subcategories: any[];
};

export default function SubcategoriesScrollbar({ categorySlug, subcategorySlug, subcategories }: Props) {
  const fullSlug = !!subcategorySlug ? `${categorySlug}/${subcategorySlug}` : categorySlug;
  const scrollAreaRef = useRef<HTMLDivElement | null>(null);
  const viewportRef = useRef<HTMLDivElement | null>(null);

  const [canLeft, setCanLeft] = useState(false);
  const [canRight, setCanRight] = useState(false);

  // --- Track scroll position for arrow visibility ---
  useLayoutEffect(() => {
    const el = scrollAreaRef.current;
    if (!el) return;

    viewportRef.current = el;

    const update = () => {
      const { scrollLeft, scrollWidth, clientWidth } = el;
      setCanLeft(scrollLeft > 4);
      setCanRight(scrollLeft + clientWidth < scrollWidth - 4);
    };

    update();
    el.addEventListener("scroll", update);
    window.addEventListener("resize", update);

    return () => {
      el.removeEventListener("scroll", update);
      window.removeEventListener("resize", update);
    };
  }, []);

  // --- Scroll by chunk when arrows are clicked ---
  const scroll = (dir: "left" | "right") => {
    const el = viewportRef.current;
    if (!el) return;
    const amount = el.clientWidth * 0.7;
    el.scrollBy({
      left: dir === "left" ? -amount : amount,
      behavior: "smooth",
    });
  };

  // --- Auto-scroll to active category when subcategorySlug changes ---
  useEffect(() => {
    if (!subcategorySlug || !viewportRef.current) return;

    const el = viewportRef.current;
    const pill = el.querySelector<HTMLButtonElement>(
      `[data-category-id="${subcategorySlug}"]`
    );
    if (!pill) return;

    pill.scrollIntoView({
      behavior: "smooth",
      block: "nearest",
      inline: "center",
    });
  }, [subcategorySlug]);

  return (
    <div className="relative w-full border-b bg-background/100 backdrop-blur">
      {/* Fade overlays */}
      <div className="pointer-events-none absolute inset-y-0 left-0 z-10 w-8 bg-gradient-to-r from-background to-transparent" />
      <div className="pointer-events-none absolute inset-y-0 right-0 z-10 w-8 bg-gradient-to-l from-background to-transparent" />

      {/* Arrows */}
      <button
        type="button"
        onClick={() => scroll("left")}
        className={`absolute left-1 top-1/2 z-20 flex h-8 w-8 md:h-6 md:w-6 -translate-y-1/2 items-center justify-center rounded-full bg-background shadow transition-opacity duration-200 hover:bg-accent ${
          canLeft ? "opacity-100" : "opacity-0 pointer-events-none"
        }`}
      >
        <ChevronLeft className="h-6 w-6 md:h-4 md:w-4" />
      </button>

      <button
        type="button"
        onClick={() => scroll("right")}
        className={`absolute right-1 top-1/2 z-20 flex h-8 w-8 md:h-6 md:w-6 -translate-y-1/2 items-center justify-center rounded-full bg-background shadow transition-opacity duration-200 hover:bg-accent ${
          canRight ? "opacity-100" : "opacity-0 pointer-events-none"
        }`}
      >
        <ChevronRight className="h-6 w-6 md:h-4 md:w-4" />
      </button>

      <div
        ref={scrollAreaRef}
        className="w-full overflow-x-auto scrollbar-hide"
      >
        <div className="flex gap-3 pt-4 md:pt-2 px-4 md:px-8 whitespace-nowrap">
          {[{id: '0', name: 'Todos'}].concat(subcategories).map((cat) => {
            const active = cat.id.toString() === subcategorySlug;
            return (
              <Link href={cat.id === '0' ? `/categorias/${categorySlug}?prev=${fullSlug}` : `/categorias/${categorySlug}/${cat.id}?prev=${fullSlug}`} key={cat.id}>
                <div
                  data-category-id={cat.id}
                  className={cn(
                    active
                      ? 'border-gray-600 text-gray-900'
                      : 'border-transparent text-gray-600 hover:border-gray-500 hover:text-gray-800',
                    'border-b-2 px-1 pb-4 md:pb-2 text-sm font-medium whitespace-nowrap cursor-pointer max-w-[8rem] truncate',
                  )}
                >
                    {cat.name}
                  </div>
              </Link>
            )
          })}
        </div>
      </div>
    </div>
  );
}
